import "./organizational_chart.css";
import { React, useState, useEffect, useContext } from 'react';
import * as api from "../../api/apiConnector.js";
import UserContext from '../../components/User_Context.js';
import Choices from '../../components/Choices.js';
import * as utils from "../utils.js";
import withReactContent from 'sweetalert2-react-content';
import Swal from 'sweetalert2';
import { useTranslation } from 'react-i18next';
import * as menu from "../../assets/menus/menu.js"
import { SystemLabels } from '../../screens/_label/system/system_labels.js';

const Organizational_Chart = (params) => {
    const { nodes, onChange, unitsTypeVoc, onSaveRequest, hospitalRegimenTypesVoc, riskAreaVoc, areaSpecialitaVoc, allowDragging } = params;
    const [nodesArray, setNodesArray] = useState(nodes);
    const [currentNode, setCurrentNode] = useState(null);
    const { currentUser } = useContext(UserContext);
    const [draggingNode, setDraggingNode] = useState(null);
    const [droppingNode, setDroppingNode] = useState(null);
    const [chartEditing, setChartEditing] = useState(null);
    const { t } = useTranslation();
    const [openNodes, setOpenNodes] = useState({});


    // Initialize i18n labels from centralized file
    const i18n = menu.getI18n();
    i18n.addResourceBundle('it', 'labels', SystemLabels.it, true, true);
    i18n.addResourceBundle('en', 'labels', SystemLabels.en, true, true);

    // Sync internal nodesArray state when nodes prop changes from parent
    useEffect(() => {
        setNodesArray(nodes);
    }, [nodes]);

    const _update = () => {
        setNodesArray(utils.clone(nodesArray));
        if (onChange) {
            onChange(nodesArray);
        }
    }

    const _getPath = (node) => {
        const output = [];
        let _current = node;
        while (_current) {
            output.push(_current);
            _current = nodesArray.find(x => x.Id == _current.ParentId);
        }

        return output.reverse();
    }

    // Date helper functions standardized with hand_hygiene system
    const parseDate = (value) => {
        if (!value) return null;
        const date = new Date(value);
        if (isNaN(date.getTime())) return null;
        // Reset seconds and milliseconds to ensure clean formatting
        date.setSeconds(0, 0);
        return date;
    };

    // Helper: format local Date into YYYY-MM-DDThh:mm for datetime-local
    const formatLocalDateTime = (date = new Date()) => {
        if (!date) return '';
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return `${year}-${month}-${day}T${hours}:${minutes}`;
    };

    // Helper: format ISO string into YYYY-MM-DDThh:mm for datetime-local (maintaining local time)
    const formatDateTimeLocal = (isoString) => {
        if (!isoString) return '';
        const date = parseDate(isoString);
        if (!date) return '';
        return formatLocalDateTime(date);
    };

    const _getChildren = (node) => {
        const output = [node];
        const _children = nodesArray.filter(x => x.ParentId == node.Id);
        if (_children) {
            for (var c of _children) {
                const _subItems = _getChildren(c);
                output.push(..._subItems);
            }
        }

        return output;
    }

    const deleteNode = async (id, level) => {
        const _node = nodesArray.find(x => x.Id == id);
        if (!_node) return;

        // Show confirmation dialog only for the root deletion (level 0 or undefined)
        if (!level) {
            const _children = nodesArray.filter(x => x.ParentId == id);
            const hasChildren = _children.length > 0;

            const confirmationText = hasChildren
                ? `${t('confirmDeleteNodeWithChildren', { ns: 'labels' })} "${_node.Name}" (${_children.length} ${_children.length === 1 ? t('subUnit', { ns: 'labels' }) : t('subUnits', { ns: 'labels' })})?`
                : `${t('confirmDeleteNode', { ns: 'labels' })} "${_node.Name}"?`;

            const confirmed = await withReactContent(Swal).fire({
                title: t('attention', { ns: 'labels' }),
                text: confirmationText,
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#d33',
                cancelButtonColor: '#aaa',
                confirmButtonText: t('delete', { ns: 'labels' }),
                cancelButtonText: t('cancel', { ns: 'labels' })
            });

            if (!confirmed.isConfirmed) {
                return; // User cancelled, do not proceed with deletion
            }
        }

        // Proceed with deletion
        _node.Deleted = true;
        _node.__toUpdate = true;

        const _children = nodesArray.filter(x => x.ParentId == id);
        for (var c of _children) {
            deleteNode(c.Id, (level || 0) + 1);
        }

        if (!level) {
            _update();

            // Show success message
            Swal.fire({
                title: t('info', { ns: 'labels' }),
                text: t('nodeMarkedForDeletion', { ns: 'labels' }),
                icon: 'info',
                timer: 3000,
                showConfirmButton: true
            });
        }
    }

    useEffect(() => {
        if (currentNode) {
            // window.$("#business_unit_edit_modal").modal("show");
        }

        if (allowDragging) {
            window.$("tr[data-id]").draggable({
                opacity: 0.7,
                cursor: "move",
                containement: "table",
                revert: "invalid",
                start: (e) => {
                    window.$(e.target).css("z-index", 1000);
                    setDraggingNode(nodesArray.find(x => x.Id == window.$(e.target).attr("data-id")));
                },
                stop: (e) => {
                    window.$(e.target).css("z-index", 0);
                    window.$("tr[data-id]").find("td").removeClass("table-primary");
                },
                axis: "y"
            });

            window.$("tr[data-id]").droppable({
                over: (e, ui) => {
                    window.$(e.target).find("td").addClass("table-primary");
                },
                out: (e, ui) => {
                    window.$(e.target).find("td").removeClass("table-primary");
                },
                drop: (e, ui) => {
                    setDroppingNode(nodesArray.find(x => x.Id == window.$(e.target).attr("data-id")));
                    ui.draggable.animate({ left: 0, top: 0 }, 500);
                }
            });

            window.$("tr[data-root-node]").droppable({
                drop: (e, ui) => {
                    setDroppingNode({ Id: null });
                    ui.draggable.animate({ left: 0, top: 0 }, 500);
                }
            });
        }
    }, [nodesArray, currentNode, allowDragging])

    useEffect(() => {
        //nop
    }, [draggingNode]);

    useEffect(() => {
        if (chartEditing) {
            _editChart(chartEditing);
        }
    }, [chartEditing]);

    useEffect(() => {
        if (draggingNode && droppingNode) {
            const _draggingPath = _getPath(draggingNode);
            const _forbidden = _getChildren(draggingNode).find(x => x.Id == droppingNode.Id);
            if (_forbidden) {

            }
            else {
                draggingNode.ParentId = droppingNode.Id;
            }
            _update();
        }
    }, [droppingNode]);

    // Cleanup effect when component unmounts
    useEffect(() => {
        return () => {
            // Clean up any remaining select2 instances on unmount
            window.$('.select2-hidden-accessible').each(function () {
                try {
                    window.$(this).select2('destroy');
                } catch (e) {
                    // Silent cleanup on unmount
                }
            });
        };
    }, []);

    const drawMainNode = () => {
        const levelOneNodes = nodesArray.filter(x => (x.ParentId == null) && !x.Deleted) ?? [];
        return (
            <>
                <tr data-root-node="true" >
                    <td></td>
                    <td></td>
                    <td>
                        <div>
                            <i style={{ cursor: 'pointer' }} data-bs-tooltip="tooltip" data-bs-placement='top' title={t('add', { ns: 'labels' })} onClick={(e) => {
                                _addNode({ node: { Id: null } });
                            }} className="fa-regular fa-square-plus me-4 fa-2xl hover-icon-add" />
                        </div>
                    </td>
                </tr>

                {(levelOneNodes.length > 0) ? (<>
                    {levelOneNodes.map(x => drawNode({ node: x, level: 1 }))}
                </>)
                    : (null)}
            </>
        )
    }

    const _addNode = async (node) => {
        const _node = await _getNewNode();
        _node.ParentId = node.Id;
        _node.Name = t('newBusinessUnit', { ns: 'labels' });
        _node.__toUpdate = true;
        nodesArray.push(_node);
        _update();
        setCurrentNode(utils.clone(_node));
    }

    const _drawIconCode = (node) => {
        const icon = unitsTypeVoc.find(x => x.Code == node.UnitTypeCode)?.Icon;

        if (icon != '' && icon != null) {
            return (
                <>
                    <i className={icon}></i>
                </>
            );
        }
        else {
            return null;
        }
    }

    // Funzione per prefisso ad albero
    const getLevelTreePrefix = (level) => {
        if (level <= 1) return null;
        let prefix = [];
        for (let i = 2; i < level; i++) {
            prefix.push(<span key={i} className="org-tree-prefix">|</span>);
        }
        if (level > 1) {
            prefix.push(<span key={'last'} className="org-tree-prefix">—</span>);
        }
        return prefix;
    };

    const drawNode = (params) => {
        const { node, level = 0 } = params;

        const children = nodesArray.filter(x => x.ParentId == node.Id && !x.Deleted) ?? [];
        const hasChildren = children.length > 0;
        const isOpen = openNodes[node.Id] ?? true;

        if (node.Deleted) {
            return (null);
        }
        else {
            return (
                <>
                    <tr data-id={node.Id} style={{}}>
                        <th scope="row">
                            {hasChildren && (
                                <i
                                    className={`fa ${isOpen ? 'fa-caret-down' : 'fa-caret-right'}`}
                                    style={{ cursor: 'pointer', marginRight: '0.5em' }}
                                    title={isOpen ? t('Chiudi figli') : t('Apri figli')}
                                    onClick={() => setOpenNodes({ ...openNodes, [node.Id]: !isOpen })}
                                />
                            )}
                            {_drawIconCode(node)}
                        </th>
                        <td style={{ whiteSpace: "wrap" }}>
                            {
                                (() => {
                                    const groupName = unitsTypeVoc.find(x => x.Code == node.UnitTypeCode)?.Name;
                                    const extras = (
                                        <>
                                            {node.Code ? (
                                                <span className="badge bg-light text-dark border ms-2">{node.Code}</span>
                                            ) : null}
                                            {groupName ? (
                                                <span className="badge bg-light text-dark border ms-2">{groupName}</span>
                                            ) : null}
                                        </>
                                    );

                                    return level < 3 ? (
                                        <h5 style={{ marginLeft: `${level * 1.5}em`, display: 'flex', alignItems: 'center' }}>
                                            {getLevelTreePrefix(level)}{node.Name} {extras}
                                        </h5>
                                    ) : (
                                        <p style={{ marginLeft: `${level * 1.5}em`, display: 'flex', alignItems: 'center' }}>
                                            {getLevelTreePrefix(level)}{node.Name} {extras}
                                        </p>
                                    );
                                })()
                            }
                        </td>
                        <td>
                            <div>
                                <i style={{ cursor: 'pointer', color: '#17a2b8' }} data-bs-tooltip="tooltip" data-bs-placement='top' title={t('edit', { ns: 'labels' })} onClick={(e) => {
                                    setCurrentNode(utils.clone(node));
                                }} className="fa-regular fa-pen-to-square me-4 fa-2xl hover-icon-edit" />

                                <i style={{ cursor: 'pointer' }} data-bs-tooltip="tooltip" data-bs-placement='top' title={t('add', { ns: 'labels' })} onClick={async (e) => {
                                    _addNode(node);
                                }} className="fa-regular fa-square-plus me-4 fa-2xl hover-icon-add" />

                                <i style={{ cursor: 'pointer', color: '#dc3545' }} data-bs-tooltip="tooltip" data-bs-placement='top' title={t('delete', { ns: 'labels' })} onClick={async (e) => {
                                    await deleteNode(node.Id);
                                }} className="fa fa-trash fa-2xl hover-icon-trash" />
                            </div>
                        </td>
                    </tr>

                    {(hasChildren && isOpen) ? (<>
                        {children.map(x => drawNode({ node: x, level: level + 1 }))}
                    </>)
                        : (null)}
                </>
            )
        }

    }

    const _editChart = (node) => {
        const url = `/admin/organization/unit_chart/${node.Id}`;

        if (nodesArray.find(x => x.__toUpdate)) {
            withReactContent(Swal).fire({
                title: t('save', { ns: 'labels' }),
                icon: "question",
                text: t('unsavedChanges', { ns: 'labels' }),
                showConfirmButton: true,
                showDenyButton: true,
                showCloseButton: false,
                confirmButtonText: t('yes', { ns: 'labels' }),
                denyButtonText: t('no', { ns: 'labels' })
            }).then((r) => {
                if (r.isConfirmed) {
                    if (onSaveRequest) {
                        onSaveRequest({
                            next: () => {
                                window.location.href = url;
                            }
                        });
                    }
                } else {
                    window.location.href = url;
                }
            });
        } else {
            window.location.href = url;
        }
    }


    const editModal = () => {
        const _validate = () => {
            if (!currentNode.Name) return false;
            if (!currentNode.UnitTypeCode) return false;
            return true;
        }

        const _localUpdate = () => {
            currentNode.__localUpdate = true;
            setCurrentNode(utils.clone(currentNode));
        }

        const _confirmEdit = () => {
            const _indexToUpdate = nodesArray.findIndex(x => x.Id == currentNode.Id);
            currentNode.__toUpdate = true;
            currentNode.__localUpdate = false;
            nodesArray[_indexToUpdate] = currentNode;
            _update();
            _closeModal();
        }

        const _closeModal = () => {
            setCurrentNode(null);
        }


        if (currentNode) {
            return (
                <>
                    <div class="modal-backdrop fade show"></div>
                    <div id="business_unit_edit_modal" className="modal fade show" style={{ display: "block" }}>
                        <div className="modal-dialog modal-xl  modal-dialog-centered">
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h5 className="modal-title">{currentNode.Name}</h5>
                                    <button type="button" className="btn-close" aria-label="Close"
                                        onClick={(e) => {
                                            _closeModal();
                                        }}
                                    ></button>
                                </div>
                                <div id="business_unit_edit_modal_body" className="modal-body">
                                    <div>
                                        <label for="business_unit_code" className="form-label">{t('code', { ns: 'labels' })}</label>
                                        <input id="business_unit_code" type="text" className="form-control" value={currentNode?.Code || ''}
                                            onChange={(e) => {
                                                currentNode.Code = e.target.value;
                                                _localUpdate();
                                            }}
                                            placeholder={t('codePlaceholder', { ns: 'labels' })} />
                                    </div>
                                    <br></br>
                                    {/* Prima riga: CodeADT + CodeGEL - Only visible for DARM profile */}
                                    {process.env.REACT_APP_PROFILE?.toLowerCase() === "darm" && (
                                        <>
                                            <div className="row">
                                                <div className="col-md-6">
                                                    <label for="business_unit_code_adt" className="form-label">{t('codeADT', { ns: 'labels' })}</label>
                                                    <input id="business_unit_code_adt" type="text" className="form-control" value={currentNode?.CodeADT || ''}
                                                        onChange={(e) => {
                                                            currentNode.CodeADT = e.target.value;
                                                            _localUpdate();
                                                        }}
                                                        placeholder={t('codeADTPlaceholder', { ns: 'labels' })} />
                                                </div>
                                                <div className="col-md-6">
                                                    <label for="business_unit_code_gel" className="form-label">{t('codeGEL', { ns: 'labels' })}</label>
                                                    <input id="business_unit_code_gel" type="text" className="form-control" value={currentNode?.CodeGEL || ''}
                                                        onChange={(e) => {
                                                            currentNode.CodeGEL = e.target.value;
                                                            _localUpdate();
                                                        }}
                                                        placeholder={t('codeGELPlaceholder', { ns: 'labels' })} />
                                                </div>
                                            </div>
                                            <br></br>
                                        </>
                                    )}
                                    {/* Seconda riga: CodeATC + CodeCND - Only visible for DARM profile */}
                                    {process.env.REACT_APP_PROFILE?.toLowerCase() === "darm" && (
                                        <>
                                            <div className="row">
                                                <div className="col-md-6">
                                                    <label for="business_unit_code_atc" className="form-label">{t('codeATC', { ns: 'labels' })}</label>
                                                    <input id="business_unit_code_atc" type="text" className="form-control" value={currentNode?.CodeATC || ''}
                                                        onChange={(e) => {
                                                            currentNode.CodeATC = e.target.value;
                                                            _localUpdate();
                                                        }}
                                                        placeholder={t('codeATCPlaceholder', { ns: 'labels' })} />
                                                </div>
                                                <div className="col-md-6">
                                                    <label for="business_unit_code_cnd" className="form-label">{t('codeCND', { ns: 'labels' })}</label>
                                                    <input id="business_unit_code_cnd" type="text" className="form-control" value={currentNode?.CodeCND || ''}
                                                        onChange={(e) => {
                                                            currentNode.CodeCND = e.target.value;
                                                            _localUpdate();
                                                        }}
                                                        placeholder={t('codeCNDPlaceholder', { ns: 'labels' })} />
                                                </div>
                                            </div>
                                            <br></br>
                                        </>
                                    )}
                                    {/* CodeLIS + Risk Area - Only visible for DARM profile */}
                                    {process.env.REACT_APP_PROFILE?.toLowerCase() === "darm" && (
                                        <>
                                            <div className="row">
                                                <div className="col-md-6">
                                                    <label for="business_unit_code_lis" className="form-label">{t('codeLIS', { ns: 'labels' })}</label>
                                                    <input id="business_unit_code_lis" type="text" className="form-control" value={currentNode?.CodeLIS || ''}
                                                        onChange={(e) => {
                                                            currentNode.CodeLIS = e.target.value;
                                                            _localUpdate();
                                                        }}
                                                        placeholder={t('codeLISPlaceholder', { ns: 'labels' })} />
                                                </div>
                                                <div className="col-md-6">
                                                    <label className="form-label">{t('riskArea', { ns: 'labels' })}</label>
                                                    <Choices
                                                        voc={riskAreaVoc || []}
                                                        dropDownParent={"business_unit_edit_modal_body"}
                                                        value={"Code"}
                                                        text={"Name"}
                                                        selected={riskAreaVoc && currentNode?.RiskAreaCode ? [riskAreaVoc.find(x => x.Code == currentNode?.RiskAreaCode)].filter(Boolean) : []}
                                                        onChange={(e) => {
                                                            currentNode.RiskAreaCode = e.selected && e.selected.length > 0
                                                                ? e.selected[0].Code
                                                                : null;
                                                            _localUpdate();
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                            <br></br>
                                        </>
                                    )}
                                    <div>
                                        <label for="business_unit_name" className="form-label">{t('name', { ns: 'labels' })}<span className="text-danger">*</span></label>
                                        <input id="business_unit_name" type="text" className="form-control" value={currentNode?.Name || ''}
                                            onChange={(e) => {
                                                currentNode.Name = e.target.value;
                                                _localUpdate();
                                            }}
                                            placeholder={t('namePlaceholder', { ns: 'labels' })} />
                                    </div>
                                    <br></br>
                                    <div class="mb-4">
                                        <label id="unitTypeLabel" className="form-label">{t('group', { ns: 'labels' })}<span className="text-danger">*</span></label>


                                        {<Choices voc={unitsTypeVoc}
                                            dropDownParent={"business_unit_edit_modal_body"}
                                            value={"Code"} text={"Name"}
                                            selected={[unitsTypeVoc.find(x => x.Code == currentNode?.UnitTypeCode)].filter(Boolean)}
                                            onChange={(e) => {
                                                currentNode.UnitTypeCode = e.selected &&
                                                    e.selected.length > 0 ? e.selected[0].Code : null;
                                                _localUpdate();
                                            }
                                            }
                                        ></Choices>}
                                    </div>

                                    {/* Show hospital regimen field only if hospitalRegimenTypesVoc is provided and not empty */}
                                    {hospitalRegimenTypesVoc && hospitalRegimenTypesVoc.length > 0 && (
                                        <div class="mb-4">
                                            <label id="regimenTypeLabel" className="form-label">{t('regimen', { ns: 'labels' })}</label>

                                            <Choices voc={hospitalRegimenTypesVoc}
                                                dropDownParent={"business_unit_edit_modal_body"}
                                                value={"Code"} text={"Name"}
                                                selected={[hospitalRegimenTypesVoc.find(x => x.Code == currentNode?.HospitalRegimenTypeCode)].filter(Boolean)}
                                                onChange={(e) => {
                                                    currentNode.HospitalRegimenTypeCode = e.selected &&
                                                        e.selected.length > 0 ? e.selected[0].Code : null;
                                                    _localUpdate();
                                                }
                                                }
                                            ></Choices>
                                        </div>
                                    )}

                                    <div class="mb-4">
                                        <div className="row">
                                            <div className="col-md-6">
                                                <label for="business_unit_start_date" className="form-label">{t('startDate', { ns: 'labels' })}</label>
                                                <input
                                                    id="business_unit_start_date"
                                                    type="datetime-local"
                                                    className="form-control"
                                                    value={formatDateTimeLocal(currentNode?.StartDate)}
                                                    onChange={(e) => {
                                                        // Store the datetime-local value as is, will be converted to proper format when saving
                                                        currentNode.StartDate = e.target.value ? new Date(e.target.value).toISOString() : null;
                                                        _localUpdate();
                                                    }}
                                                />
                                            </div>
                                            <div className="col-md-6">
                                                <label for="business_unit_end_date" className="form-label">{t('endDate', { ns: 'labels' })}</label>
                                                <input
                                                    id="business_unit_end_date"
                                                    type="datetime-local"
                                                    className="form-control"
                                                    value={formatDateTimeLocal(currentNode?.EndDate)}
                                                    min={formatDateTimeLocal(currentNode?.StartDate)}
                                                    onChange={(e) => {
                                                        // Store the datetime-local value as is, will be converted to proper format when saving
                                                        currentNode.EndDate = e.target.value ? new Date(e.target.value).toISOString() : null;
                                                        _localUpdate();
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <div class="mb-4">
                                        <div className="row">
                                            <div className="col-md-6">
                                                <label for="business_unit_percentage" className="form-label">{t('percentage', { ns: 'labels' })}</label>
                                                <input id="business_unit_percentage" type="number" className="form-control" value={currentNode?.Percentage || null}
                                                    onChange={(e) => {
                                                        currentNode.Percentage = e.target.value;
                                                        _localUpdate();
                                                    }}
                                                    placeholder={t('percentage', { ns: 'labels' })} />
                                            </div>
                                            <div className="col-md-6">
                                                <label className="form-label">{t('areaSpecialty', { ns: 'labels' })}</label>
                                                <Choices
                                                    voc={areaSpecialitaVoc || []}
                                                    dropDownParent={"business_unit_edit_modal_body"}
                                                    value={"SpecialtyCode"}
                                                    text={"SpecialtyDescription"}
                                                    selected={areaSpecialitaVoc && currentNode?.SpecialtyCode ? [areaSpecialitaVoc.find(x => x.SpecialtyCode == currentNode?.SpecialtyCode)].filter(Boolean) : []}
                                                    onChange={(e) => {
                                                        currentNode.SpecialtyCode = e.selected && e.selected.length > 0
                                                            ? e.selected[0].SpecialtyCode
                                                            : null;
                                                        _localUpdate();
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {process.env.REACT_APP_LIS_ALERT_CONFIGURATION === 'true' && (
                                        <div className="mb-4">
                                            <div className="form-check form-switch">
                                                <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    id="business_unit_ou_lis_allow_send_alert"
                                                    checked={!!currentNode?.OULISAllowSendAlert}
                                                    onChange={(e) => {
                                                        currentNode.OULISAllowSendAlert = e.target.checked;
                                                        _localUpdate();
                                                    }}
                                                />
                                                <label className="form-check-label" htmlFor="business_unit_ou_lis_allow_send_alert">
                                                    {t('allowSendAlert', { ns: 'labels' })}
                                                </label>
                                            </div>
                                        </div>
                                    )}

                                    <hr></hr>
                                    <button className="btn btn-link" onClick={(e) => {
                                        if (currentNode.__localUpdate) {
                                            withReactContent(Swal).fire({
                                                title: t('update', { ns: 'labels' }),
                                                icon: "question",
                                                text: t('confirmChanges', { ns: 'labels' }),
                                                showConfirmButton: true,
                                                showDenyButton: true,
                                                showCloseButton: false,
                                                confirmButtonText: t('yes', { ns: 'labels' }),
                                                denyButtonText: t('no', { ns: 'labels' })
                                            }).then((r) => {
                                                if (r.isConfirmed) {
                                                    _confirmEdit();
                                                    setChartEditing(currentNode);
                                                } else {
                                                    setChartEditing(currentNode);
                                                }
                                            });
                                        }
                                        else {
                                            _editChart(currentNode);
                                        }
                                    }}>{t('editOrgChart', { ns: 'labels' })}</button>
                                </div>
                                <div className="modal-footer">
                                    <button type="button" className="btn btn-secondary"
                                        onClick={(e) => {
                                            _closeModal();
                                        }}
                                    >{t('close', { ns: 'labels' })}</button>
                                    <button type="button" onClick={(e) => {
                                        _confirmEdit();
                                    }}
                                        className="btn btn-primary" disabled={_validate ? null : true}>{t('ok', { ns: 'labels' })}</button>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )
        }
        else {
            return (null);
        }

    }

    const _getNewNode = async () => {
        const _node = (await api.sendMessage("admin/system/organization/getNewBusinessUnit", {
            user: currentUser()
        }))?.node;

        _node.UnitTypeCode = unitsTypeVoc[0].Code;
        return _node;
    }

    return (
        <>
            {
                editModal()
            }
            <table className="table">
                <thead>
                    <tr>
                        <th className="col-md-1" scope="col">#</th>
                        <th className="col-md-11">{t('name', { ns: 'labels' })}</th>
                        <th className="col-md-1">{t('action', { ns: 'labels' })}</th>
                    </tr>
                </thead>
                <tbody id="tBodyTable">
                    {drawMainNode()}
                    {/* {drawNode({})} */}

                </tbody>
            </table>
        </>
    );
}

export { Organizational_Chart }